import React, { useState, useMemo, useEffect, useCallback } from "react"
import { Button, Flex } from "antd"
import { useTitle } from "hooks/useTitle"
import { useTranslation } from "react-i18next"
import { PlusOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import styles from "./staff.module.css"
import StaffTable from "./StaffTable"
import SearchInput from "ui/SearchInput"
import RolesAccess from "./RolesAccess"
import AddStaffModal from "./AddStaffModal"
import AddStaffFullScreen from "./AddStaffModal/AddStaffFullScreen"
import Tabs from "ui/Tabs"
import { debounce } from "lodash"
import {
  getStaffMembers,
  selectStaffMembers,
  selectStaffMembersLoading,
  selectStaffMembersMetadata,
  getRoles,
  selectRoles
} from "store/staff"
import CustomPagination from "components/common/CustomPagination"
import MultiSelect from "components/common/MultiSelect"
import FilterChips from "ui/FilterChips"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { FilterIcon } from "icons"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const DELAY_TIME = 300

const Staff = () => {
  useTitle("Staff")
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isMobile, isTablet } = useDevice()
  const { hasPermission } = usePermissions()
  const staffMembers = useSelector(selectStaffMembers)
  const staffLoading = useSelector(selectStaffMembersLoading)
  const metadata = useSelector(selectStaffMembersMetadata)
  const roles = useSelector(selectRoles)
  const userRole = useSelector((state) => state.user.role)

  const canCreateStaff = hasPermission(MODULES.STAFF, ACTIONS.CREATE)
  const isStaffUser = userRole === "staff_user"

  const [activeTab, setActiveTab] = useState("staff")
  const [searchInput, setSearchInput] = useState("")
  const [searchBy, setSearchBy] = useState("")
  const [selectedRoles, setSelectedRoles] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false)
  const [editingStaffId, setEditingStaffId] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortBy, setSortBy] = useState("")
  const [sortDirection, setSortDirection] = useState("asc")
  const [mobileStaffList, setMobileStaffList] = useState([])
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [tempSelectedRoles, setTempSelectedRoles] = useState([])
  const [tempSelectedStatuses, setTempSelectedStatuses] = useState([])

  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  useEffect(() => {
    if (activeTab !== "staff") return

    const params = {
      page: currentPage,
      per_page: pageSize
    }

    if (selectedStatuses.length > 0 && !selectedStatuses.includes("all")) {
      if (selectedStatuses.length === 1) {
        params.status = selectedStatuses[0]
      } else {
        params["status[]"] = selectedStatuses
      }
    }

    if (selectedRoles.length > 0 && !selectedRoles.includes("all")) {
      params["role_ids[]"] = selectedRoles
    }

    if (searchBy.trim()) {
      params.search = searchBy.trim()
    }

    if (sortBy) {
      params.sort_by = sortBy
      params.sort_direction = sortDirection
    }

    dispatch(getStaffMembers(params))
  }, [dispatch, currentPage, pageSize, selectedRoles, selectedStatuses, searchBy, sortBy, sortDirection, activeTab])

  useEffect(() => {
    if (isMobile && staffMembers.length > 0) {
      if (currentPage === 1) {
        setMobileStaffList(staffMembers)
      } else {
        setMobileStaffList((prev) => [...prev, ...staffMembers])
      }
    }
  }, [staffMembers, currentPage, isMobile])

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchBy(value)
        setCurrentPage(1)
        setMobileStaffList([])
      }, DELAY_TIME),
    []
  )

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel()
    }
  }, [debouncedSetSearch])

  const handleSearchChange = (value) => {
    setSearchInput(value)
    if (!value || value.trim() === "") {
      setSearchBy("")
      setCurrentPage(1)
      setMobileStaffList([])
    } else {
      debouncedSetSearch(value)
    }
  }

  const handleRolesChange = (roleIds) => {
    setSelectedRoles(roleIds)
    setCurrentPage(1)
    if (isMobile) {
      setMobileStaffList([])
    }
  }

  const handleStatusesChange = (statuses) => {
    setSelectedStatuses(statuses)
    setCurrentPage(1)
    if (isMobile) {
      setMobileStaffList([])
    }
  }

  const handleOpenFilterDrawer = () => {
    setTempSelectedRoles(selectedRoles)
    setTempSelectedStatuses(selectedStatuses)
    setFilterDrawerOpen(true)
  }

  const handleApplyFilters = () => {
    setSelectedRoles(tempSelectedRoles)
    setSelectedStatuses(tempSelectedStatuses)
    setCurrentPage(1)
    setMobileStaffList([])
    setFilterDrawerOpen(false)
  }

  const handleClearFilters = () => {
    setTempSelectedRoles([])
    setTempSelectedStatuses([])
  }

  const activeFilterCount = useMemo(() => {
    let count = 0
    const filteredRoles = selectedRoles?.filter((r) => r !== "all")
    const filteredStatuses = selectedStatuses?.filter((s) => s !== "all")
    count += filteredRoles?.length
    count += filteredStatuses?.length
    return count
  }, [selectedRoles, selectedStatuses])

  const handleAddStaff = () => {
    setIsStaffModalOpen(true)
  }

  const handleEditStaff = (staffId) => {
    setEditingStaffId(staffId)
    setIsStaffModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsStaffModalOpen(false)
    setEditingStaffId(null)
  }

  const refreshStaffList = useCallback(() => {
    const params = {
      page: currentPage,
      per_page: pageSize
    }

    if (selectedStatuses.length > 0 && !selectedStatuses.includes("all")) {
      if (selectedStatuses.length === 1) {
        params.status = selectedStatuses[0]
      } else {
        params["status[]"] = selectedStatuses
      }
    }

    if (selectedRoles.length > 0 && !selectedRoles.includes("all")) {
      params["role_ids[]"] = selectedRoles
    }

    if (searchBy.trim()) {
      params.search = searchBy.trim()
    }

    if (sortBy) {
      params.sort_by = sortBy
      params.sort_direction = sortDirection
    }

    dispatch(getStaffMembers(params))
  }, [dispatch, currentPage, pageSize, selectedStatuses, selectedRoles, searchBy, sortBy, sortDirection])

  const handleSaveStaff = async () => {
    await refreshStaffList()
    setIsStaffModalOpen(false)
    setEditingStaffId(null)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (size) => {
    setPageSize(size)
    setCurrentPage(1)
  }

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1)
  }

  const handleTableChange = (_pagination, _filters, sorter) => {
    if (sorter?.columnKey) {
      const sortField = sorter.columnKey
      const sortOrder = sorter.order

      if (!sortOrder) {
        setSortBy("")
        setSortDirection("asc")
        setCurrentPage(1)
      } else {
        setSortBy(sortField)
        setSortDirection(sortOrder === "ascend" ? "asc" : "desc")
        setCurrentPage(1)
      }
    } else {
      setSortBy("")
      setSortDirection("asc")
      setCurrentPage(1)
    }
  }

  const roleOptions = useMemo(() => {
    if (!roles || roles.length === 0) return []

    return [
      { value: "all", label: t("staff.filters.all_roles") },
      ...roles.map((role) => ({
        value: role.id,
        label: role.name
      }))
    ]
  }, [roles, t])

  const statusOptions = useMemo(
    () => [
      { value: "all", label: t("staff.filters.all_statuses") },
      { value: "active", label: t("staff.status.active") },
      { value: "deactivated", label: t("staff.status.deactivated") }
    ],
    [t]
  )

  const tabItems = useMemo(() => {
    const tabs = [{ key: "staff", label: t("staff.tabs.staff") }]

    if (!isStaffUser) {
      tabs.push({ key: "roles", label: t("staff.tabs.roles_access") })
    }

    return tabs
  }, [t, isStaffUser])

  return (
    <>
      {!((isMobile || isTablet) && isStaffModalOpen) && (
        <div className={styles.customTabs}>
          <Tabs tabs={tabItems} activeTab={activeTab} onTabChange={setActiveTab} className={styles.mobileTabs} />
          <div className={styles.tabContent}>
            {activeTab === "staff" && (
              <>
                {isMobile ? (
                  <>
                    {canCreateStaff && (
                      <Button
                        type="primary"
                        onClick={handleAddStaff}
                        aria-label={t("staff.add_staff")}
                        size="large"
                        icon={<PlusOutlined aria-hidden="true" />}
                        className={styles.mobileAddStaffButton}
                        block
                      >
                        {t("staff.add_staff")}
                      </Button>
                    )}
                    <Flex gap={12} align="center" className={`mb-m ${styles.mobileSearchRow}`}>
                      <SearchInput
                        searchBy={searchInput}
                        setSearchBy={handleSearchChange}
                        placeholder={t("staff.search_staff")}
                        ariaLabel={t("staff.search_staff")}
                        ariaControls="staff-search-results"
                        className={styles.mobileSearchInput}
                      />
                      <div className={styles.filterButtonWrapper}>
                        <Button
                          className={styles.mobileFilterButton}
                          onClick={handleOpenFilterDrawer}
                          aria-label={t("staff.filters.title")}
                        >
                          <FilterIcon width={20} height={20} />
                        </Button>
                        {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
                      </div>
                    </Flex>
                    <Flex gap={8} wrap className="mb-m">
                      <FilterChips selectedValues={selectedRoles} options={roleOptions} onChange={handleRolesChange} />
                      <FilterChips selectedValues={selectedStatuses} options={statusOptions} onChange={handleStatusesChange} />
                    </Flex>
                  </>
                ) : (
                  <>
                    <Flex justify="space-between" wrap="wrap" gap={10} className={`mb-xl ${styles.filterWrap}`}>
                      <Flex gap={16} wrap align="center" className={`flex-1 ${styles.searchAndFilters}`}>
                        <SearchInput
                          searchBy={searchInput}
                          setSearchBy={handleSearchChange}
                          placeholder={t("staff.search_staff")}
                          ariaLabel={t("staff.search_staff")}
                          ariaControls="staff-search-results"
                          className={styles.searchInput}
                        />
                        <MultiSelect
                          value={selectedRoles}
                          onChange={handleRolesChange}
                          options={roleOptions}
                          placeholder={t("staff.filters.role")}
                        />
                        <MultiSelect
                          value={selectedStatuses}
                          onChange={handleStatusesChange}
                          options={statusOptions}
                          placeholder={t("staff.filters.status")}
                        />
                        {canCreateStaff && (
                          <Button
                            type="primary"
                            onClick={handleAddStaff}
                            aria-label={t("staff.add_staff")}
                            size="large"
                            icon={<PlusOutlined aria-hidden="true" />}
                            className={styles.addStaffButton}
                          >
                            {t("staff.add_staff")}
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                    <Flex gap={16} wrap className="mt-s">
                      <FilterChips selectedValues={selectedRoles} options={roleOptions} onChange={handleRolesChange} />
                      <FilterChips selectedValues={selectedStatuses} options={statusOptions} onChange={handleStatusesChange} />
                    </Flex>
                  </>
                )}
                <div className="commonGrid">
                  <StaffTable
                    staff={staffMembers}
                    mobileStaff={mobileStaffList}
                    loading={staffLoading}
                    sortField={sortBy}
                    sortOrder={sortBy ? (sortDirection === "asc" ? "ascend" : "descend") : null}
                    handleTableChange={handleTableChange}
                    onEdit={handleEditStaff}
                    onRefresh={refreshStaffList}
                    currentPage={currentPage}
                    totalPages={Math.ceil((metadata?.total_results || 0) / pageSize)}
                    onLoadMore={handleLoadMore}
                    totalResults={metadata?.total_results || 0}
                  />
                  {!isMobile && staffMembers.length > 0 && metadata && (
                    <CustomPagination
                      metadata={metadata}
                      pageSize={pageSize}
                      total={metadata.total_results || 0}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  )}
                </div>
              </>
            )}
            {activeTab === "roles" && <RolesAccess />}
          </div>
        </div>
      )}
      {isMobile || isTablet ? (
        <AddStaffFullScreen
          open={isStaffModalOpen}
          onCancel={handleCloseModal}
          onSave={handleSaveStaff}
          staffId={editingStaffId}
          editMode={!!editingStaffId}
        />
      ) : (
        <AddStaffModal
          open={isStaffModalOpen}
          onCancel={handleCloseModal}
          onSave={handleSaveStaff}
          staffId={editingStaffId}
          editMode={!!editingStaffId}
        />
      )}
      <CustomDrawer open={filterDrawerOpen} setOpen={setFilterDrawerOpen} title={t("staff.filters.title")}>
        <Flex vertical gap={16} className={styles.filterDrawerContent}>
          <MultiSelect
            value={tempSelectedRoles}
            onChange={setTempSelectedRoles}
            options={roleOptions}
            placeholder={t("staff.filters.role")}
            style={{ minWidth: "100%", maxWidth: "100%" }}
          />
          <MultiSelect
            value={tempSelectedStatuses}
            onChange={setTempSelectedStatuses}
            options={statusOptions}
            placeholder={t("staff.filters.status")}
            style={{ minWidth: "100%", maxWidth: "100%" }}
          />
          <Button type="primary" block size="large" onClick={handleApplyFilters} className={styles.applyFilterButton}>
            {t("staff.filters.apply")}
          </Button>
          <Button block size="large" onClick={handleClearFilters} className={styles.clearFilterButton}>
            {t("staff.filters.clear_all")}
          </Button>
        </Flex>
      </CustomDrawer>
    </>
  )
}

export default Staff
